/**
 * Rock Cerchar — field metadata, options, and defaults (data only, no JSX, per
 * CODING-STANDARDS). Mirrors rock-bts/rockBtsFields.ts.
 *
 * Dropdown domains trace to the backend constants (backend/src/constants/
 * rockCerchar.ts) and the 2026-06-26 requirements package. The setup/per-stylus
 * option lists are re-declared here as FE copies (no shared FE/BE module yet) so
 * every dropdown in JSX reads from this file, never an inline array.
 */
import type { CercharGeneral, CercharStylus } from '../../../types/rockCerchar';

// ── CAI classification guide popup rows (REQ-05 / D6) ─────────────────────────
// Display copy for the "View Classification Guide" dialog on the Result Entry
// tab (re-added per BUG-2026-0142 rework). Labels + ranges mirror the confirmed
// classification bands — kept as a separate display list so the dialog JSX reads
// its rows from this file rather than an inline array.
export interface CercharGuideRow {
  range: string;
  label: string;
}

export const CERCHAR_CLASSIFICATION_GUIDE_ROWS: readonly CercharGuideRow[] = [
  { range: '0.0 – 0.5', label: 'Very Low Abrasiveness' },
  { range: '0.5 – 1.0', label: 'Low Abrasiveness' },
  { range: '1.0 – 2.0', label: 'Medium Abrasiveness' },
  { range: '2.0 – 4.0', label: 'High Abrasiveness' },
  { range: '4.0 – 6.0', label: 'Extreme Abrasiveness' },
  { range: '> 6.0', label: 'Quartzite' },
] as const;

// ── Setup dropdowns (REQ-01) — FE copies of the backend domains ──────────────
export const CERCHAR_SPECIMEN_CONDITIONS = [
  'As received',
  'Saturated',
  'Air dried',
  'Oven dried',
] as const;

export const CERCHAR_SURFACE_CONDITIONS = [
  'Saw cut surface',
  'Freshly broken surface',
] as const;

// ── Per-stylus dropdowns (REQ-03) — used by the Result Entry grid (Phase 4b-ii) ─
export const CERCHAR_DIRECTIONS = ['Perpendicular', 'Parallel', '-'] as const;
// Fixed-but-editable list (item 4.8): a free-text "other" value is allowed on top
// of these canonical options (the Result Entry dropdown adds it back transiently).
export const CERCHAR_PLANES_OF_WEAKNESS = ['Bedding', 'Foliation', 'Vein', '-'] as const;

// ── Fixed standard parameters (D4 / REQ-06) — read-only info block ───────────
// (BUG-2026-0142 removed the Test Method + Equipment inputs from the UI; the
// classification-guide dialog and the batch-grid metadata that lived here went
// with the retired sections/ components.)
export const CERCHAR_TEST_EQUIPMENT = 'West CERCHAR';
export const CERCHAR_TEST_VELOCITY = '1 mm/s over 10 mm';
export const CERCHAR_STYLUS_HRC = '55 HRC';

/** Cerchar always has exactly 5 styluses (backend back-fills 1–5). */
const CERCHAR_STYLUS_COUNT = 5;

// ── Defaults ─────────────────────────────────────────────────────────────────
export const EMPTY_CERCHAR_GENERAL: CercharGeneral = {
  testMethod: null,
  specimenDescription: null,
  specimenCondition: null,
  testSurfaceCondition: null,
  planeOfWeaknessOrientation: null,
  stylusTipsChecked: null,
  cercharMachine: null,
  leebHardnessGauge: null,
  calibrationCertificate: null,
  additionalEquipment: null,
  leebHardnessEnds: null,
  leebHardnessEdge: null,
  testDate: null,
  testedBy: null,
  remarksText: null,
  additionalObservations: null,
};

export const emptyStylus = (stylusNumber: number): CercharStylus => ({
  stylusNumber,
  diameter1Mm: null,
  diameter2Mm: null,
  directionToPlane: null,
  planeOfWeakness: null,
});

export const emptyStyluses = (): CercharStylus[] =>
  Array.from({ length: CERCHAR_STYLUS_COUNT }, (_, i) => emptyStylus(i + 1));
